import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { workOrderService } from '../../../services/workOrderService';
import { SUBSCRIPTION_QUERY_KEY } from '../../../hooks/useSubscription';
import type { CreateWorkOrderData, WorkOrder } from '../../../types/workOrder';
import { useLanguage } from '../../../features/language/useLanguage';
import { isQuotaExceededError, type QuotaExceededPayload } from '../quotaError';
import type { BulkCardSnapshot, WorkOrderCardFormDefaults } from './useWorkOrderCardForm';
import type { BulkCardHandle, BulkStage } from './BulkWorkOrderCard';
import type { BulkCardIssue } from './bulkCardIssues';

export const MAX_BULK_CARDS = 20;

export interface BulkCard {
  id: string;
  workOrder: WorkOrder | null;
  defaults?: WorkOrderCardFormDefaults;
}

export interface BulkPendingConfirm {
  ready: number;
  pendingCodes: string[];
}

export interface BulkBlockedCard {
  /** 0-based card position; names the card when it has no WO number. */
  index: number;
  code: string;
  issues: BulkCardIssue[];
}

const newCardId = () => `bulk-card-${Math.random().toString(36).slice(2, 10)}`;

const errorMessage = (err: unknown): string => {
  const response = (err as { response?: { data?: { error?: string; message?: string } } })?.response;
  return response?.data?.error || response?.data?.message || (err as Error)?.message || 'Unknown error';
};

export const useBulkWorkOrders = ({
  onQuotaExceeded,
  onDone,
}: {
  onQuotaExceeded: (payload: QuotaExceededPayload) => void;
  onDone: (message: string) => void;
}) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const [cards, setCards] = useState<BulkCard[]>([{ id: newCardId(), workOrder: null }]);
  const [snapshots, setSnapshots] = useState<Record<string, BulkCardSnapshot>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<BulkPendingConfirm | null>(null);
  const [blockedCards, setBlockedCards] = useState<BulkBlockedCard[] | null>(null);

  const handles = useRef(new Map<string, BulkCardHandle>());

  const registerHandle = useCallback((cardId: string, handle: BulkCardHandle | null) => {
    if (handle) handles.current.set(cardId, handle);
    else handles.current.delete(cardId);
  }, []);

  const onSnapshotChange = useCallback((cardId: string, snapshot: BulkCardSnapshot) => {
    setSnapshots((prev) => {
      const previous = prev[cardId];
      // The card publishes on every render; only a real change may re-render
      // the whole panel, or the two would ping-pong forever.
      if (
        previous &&
        previous.isComplete === snapshot.isComplete &&
        JSON.stringify(previous.issues) === JSON.stringify(snapshot.issues) &&
        JSON.stringify(previous.allocations) === JSON.stringify(snapshot.allocations)
      ) {
        return prev;
      }
      return { ...prev, [cardId]: snapshot };
    });
  }, []);

  const stage: BulkStage = cards.every((c) => c.workOrder) ? 'schedule' : 'draft';

  const readyCount = useMemo(
    () => cards.filter((c) => snapshots[c.id]?.isComplete).length,
    [cards, snapshots],
  );

  const addCard = useCallback(() => {
    setErrorBanner(null);
    setCards((prev) => {
      if (prev.length >= MAX_BULK_CARDS) return prev;
      // Roasteries batch on one machine, so a new card starts from the last
      // one's machine, roaster, date and time. Notes always start empty.
      const last = prev[prev.length - 1];
      const lastValues = last ? handles.current.get(last.id)?.peek() : undefined;
      const defaults: WorkOrderCardFormDefaults | undefined = lastValues
        ? {
            machine: lastValues.machine,
            roaster: lastValues.roaster,
            machineTypeId: lastValues.machineTypeId,
            roasterId: lastValues.roasterId,
            targetDate: lastValues.targetDate,
            time: lastValues.time,
          }
        : undefined;
      return [...prev, { id: newCardId(), workOrder: null, defaults }];
    });
  }, []);

  const removeCard = useCallback((cardId: string) => {
    setErrorBanner(null);
    setCards((prev) => (prev.length <= 1 ? prev : prev.filter((c) => c.id !== cardId)));
    setSnapshots((prev) => {
      const next = { ...prev };
      delete next[cardId];
      return next;
    });
  }, []);

  const createAllDrafts = useCallback(async () => {
    setErrorBanner(null);
    setIsSubmitting(true);
    try {
      const pending = cards.filter((c) => !c.workOrder);
      const collected = await Promise.all(
        pending.map((card) => handles.current.get(card.id)?.collect()),
      );

      // Validate every card before writing anything — a half-created batch
      // because card 4 was missing a machine is worse than no batch at all.
      if (collected.some((c) => !c || !c.basicInfo)) {
        setErrorBanner(t('workOrders.bulk.fixFieldsFirst'));
        return;
      }

      let createdCount = 0;
      for (let i = 0; i < pending.length; i += 1) {
        const card = pending[i];
        const basicInfo = collected[i]!.basicInfo!;
        const payload: CreateWorkOrderData = {
          machine: basicInfo.machine,
          roaster: basicInfo.roaster,
          machineTypeId: basicInfo.machineTypeId,
          roasterId: basicInfo.roasterId,
          date: basicInfo.targetDate,
          time: basicInfo.time,
          itemId: '',
          inputWeight: 0,
          outputWeight: 0,
          useRoastProfile: false,
          lots: [],
          internalNotes: basicInfo.internalNotes,
        };
        try {
          const created = await workOrderService.create(payload);
          createdCount += 1;
          setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, workOrder: created } : c)));
        } catch (err) {
          if (isQuotaExceededError(err)) {
            onQuotaExceeded(err.response.data);
            queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
            return;
          }
          // Everything created so far keeps its work order; pressing Save as
          // Draft again re-posts only the cards that still have none.
          setErrorBanner(
            t('workOrders.bulk.createFailed', {
              index: cards.findIndex((c) => c.id === card.id) + 1,
              message: errorMessage(err),
              created: createdCount,
            }),
          );
          return;
        }
      }
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    } finally {
      setIsSubmitting(false);
    }
  }, [cards, onQuotaExceeded, queryClient, t]);

  const runSchedule = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const collected = await Promise.all(cards.map((card) => handles.current.get(card.id)?.collect()));
      if (collected.some((c) => !c || !c.basicInfo)) {
        setErrorBanner(t('workOrders.bulk.fixFieldsFirst'));
        return;
      }

      let scheduled = 0;
      let leftAsDraft = 0;

      for (let i = 0; i < cards.length; i += 1) {
        const card = cards[i];
        const data = collected[i]!;
        const basicInfo = data.basicInfo!;
        const workOrder = card.workOrder;
        if (!workOrder) continue;

        const base = {
          machine: basicInfo.machine,
          roaster: basicInfo.roaster,
          machineTypeId: basicInfo.machineTypeId,
          roasterId: basicInfo.roasterId,
          date: basicInfo.targetDate,
          time: basicInfo.time,
          internalNotes: basicInfo.internalNotes,
        };

        if (!data.isComplete) {
          // No lots and no weights: the backend touches neither stock nor the
          // quota, so this call can never fail the batch. The bean choice is
          // still worth keeping, hence the write.
          try {
            await workOrderService.updateDraftBasics(workOrder.id, { ...base, itemId: data.itemId });
          } catch (err) {
            console.error('Failed to save incomplete bulk card', workOrder.workOrderNumber, err);
          }
          leftAsDraft += 1;
          continue;
        }

        try {
          await workOrderService.update(workOrder.id, {
            ...base,
            itemId: data.itemId,
            inputWeight: data.inputWeight,
            outputWeight: data.outputWeight,
            useRoastProfile: false,
            roastType: data.roastType,
            lots: data.lots,
          });
          await workOrderService.updateStatus(workOrder.id, 'scheduled');
          scheduled += 1;
        } catch (err) {
          if (isQuotaExceededError(err)) {
            onQuotaExceeded(err.response.data);
            queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
            return;
          }
          setErrorBanner(
            t('workOrders.bulk.scheduleFailed', {
              code: workOrder.workOrderNumber || workOrder.id,
              message: errorMessage(err),
            }),
          );
          return;
        }
      }

      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
      onDone(t('workOrders.bulk.scheduleResult', { scheduled, draft: leftAsDraft }));
    } finally {
      setIsSubmitting(false);
    }
  }, [cards, onDone, onQuotaExceeded, queryClient, t]);

  const scheduleAll = useCallback(async () => {
    setErrorBanner(null);
    // A card the user started but left short of a required field (roast type,
    // a lot, …) is refused outright, same as the single form. Only untouched
    // cards take the "stays as draft" path below.
    const blocked = cards
      .map((c, index) => ({
        index,
        code: c.workOrder?.workOrderNumber || '',
        issues: snapshots[c.id]?.issues ?? [],
      }))
      .filter((c) => c.issues.length > 0);
    if (blocked.length > 0) {
      setBlockedCards(blocked);
      return;
    }
    const pendingCodes = cards
      .filter((c) => !snapshots[c.id]?.isComplete)
      .map((c) => c.workOrder?.workOrderNumber || '');
    if (pendingCodes.length > 0) {
      setPendingConfirm({ ready: cards.length - pendingCodes.length, pendingCodes });
      return;
    }
    await runSchedule();
  }, [cards, snapshots, runSchedule]);

  const confirmSchedule = useCallback(async () => {
    setPendingConfirm(null);
    await runSchedule();
  }, [runSchedule]);

  return {
    cards,
    stage,
    snapshots,
    readyCount,
    isSubmitting,
    errorBanner,
    pendingConfirm,
    setPendingConfirm,
    blockedCards,
    setBlockedCards,
    addCard,
    removeCard,
    registerHandle,
    onSnapshotChange,
    createAllDrafts,
    scheduleAll,
    confirmSchedule,
  };
};
